import { FC } from 'react';
import styled, { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { CommonPanel } from '../CommonPanel';
import { InfoButton } from '../InfoButton';
import { BuyButton } from '../BuyButton';
import { PlayersCards } from '../PlayersCards';
import { PrimaryPanelText } from '../PrimaryPanelText';
import { GamePart } from '../GamePart';
import { GameType } from '../GameType';
import { BingoCardPattern } from '../BingoCardPattern';
import { NextGamePanelProps } from './mobile';
import { LiveIndicator } from '../LiveIndicator';

const NextGamePanelContainer = styled(CommonPanel)`
    position: relative;
    height: auto;
    margin-bottom: 0.5rem;
    width: 89.8%;
`;

const NextGamePart = styled(GamePart)`
    width: 100%;
    justify-content: flex-end;
`;

const UpperContainer = styled.div`
    display: flex;
    justify-content: flex-start;
    align-items: center;
    width: 100%;
    margin-bottom: 0.5rem;
`;

const UpperTextContainer = styled.div<{ isGameLive: boolean }>`
    display: flex;
    flex-direction: column;
    flex-basis: ${({ isGameLive }) => (isGameLive ? '64.5%' : '71.5%')};
    margin-right: 1rem;
`;

const UpperImageContainer = styled.div<{ isGameLive: boolean }>`
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-basis: ${({ isGameLive }) => (isGameLive ? '21%' : '14%')};

    & > div {
        margin-right: 0.375rem;
    }
`;

const Divider = styled.hr`
    width: 100%;
    margin-left: 0;
    border-width: 0;
    height: 0.125rem;
    background-color: ${({ theme: { primaryPanel } }) =>
        primaryPanel.color.divider};
`;

const LowerContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 87%;
`;

const LowerTextContainer = styled.div<{ gameType: number }>`
    display: flex;
    justify-content: center;
    flex-direction: column;
`;

const FlexRowWrapper = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
`;

const FlexColWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 0.25rem;
`;

const PlayersCardsWrapper = styled(PlayersCards)`
    height: 2.5rem;
`;

const BuyButtonWrapper = styled(BuyButton)`
    position: absolute;
    top: 0;
    right: -7rem;
    bottom: 0;
    margin: auto 0;
`;

export const GameContinuedIcon = styled.img`
    width: 2rem;
    height: 2rem;
    align-self: flex-end;
`;

export const NextGamePanelDesktop: FC<NextGamePanelProps> = ({
    to,
    seconds,
    gameTitle,
    patternName,
    ticketPrice,
    gamePrize,
    gamePlayers,
    gameCards,
    hasJackpot,
    jackpotName,
    jackpotCall,
    jackpotPrize,
    currentPart,
    gameParts,
    gameType,
    patternType,
    parts,
    gameLinked,
    gameContinued,
    showTotalTickets,
    autoBuy,
    onInfoBox,
    onBuy,
    isGameLive,
    isPatternNameRendered,
    ...props
}) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const { starImage4 } = theme.images;
    const NEXTGAME_GAME = t('infoBox.nextGame');

    return (
        <NextGamePanelContainer {...props} type="primary">
            <UpperContainer>
                <UpperTextContainer isGameLive={isGameLive}>
                    <PrimaryPanelText
                        label={NEXTGAME_GAME}
                        value={gameTitle}
                        type="big"
                        gameLinked={gameLinked}
                    />
                    <Divider />
                </UpperTextContainer>
                <UpperImageContainer isGameLive={isGameLive}>
                    {isGameLive && <LiveIndicator />}
                    <InfoButton handleClick={onInfoBox} />
                    <GameType gameType={gameType} />
                </UpperImageContainer>
            </UpperContainer>
            <LowerContainer>
                <LowerTextContainer gameType={gameType}>
                    {isPatternNameRendered && (
                        <PrimaryPanelText
                            label={patternName}
                            value={gamePrize}
                            type="small"
                        />
                    )}
                    {hasJackpot && (
                        <PrimaryPanelText
                            label={t('infoBox.jackpotFormatting', {
                                jpName: jackpotName,
                                jpCall: jackpotCall,
                            })}
                            value={jackpotPrize || ''}
                            type="small"
                        />
                    )}
                </LowerTextContainer>
                <FlexColWrapper>
                    {gameContinued ? (
                        <GameContinuedIcon
                            src={starImage4}
                            alt="Continued Game Icon"
                        />
                    ) : (
                        <NextGamePart
                            gameParts={gameParts}
                            currentPart={currentPart}
                        />
                    )}

                    <FlexRowWrapper>
                        <BingoCardPattern
                            patternType={patternType}
                            parts={parts}
                            currentPart={currentPart - 1}
                            gameType={gameType}
                        />
                        {showTotalTickets && (
                            <>
                                <PlayersCardsWrapper
                                    type="players"
                                    text={gamePlayers}
                                />
                                <PlayersCardsWrapper
                                    type="cards"
                                    text={gameCards}
                                />
                            </>
                        )}
                    </FlexRowWrapper>
                </FlexColWrapper>
            </LowerContainer>
            <BuyButtonWrapper
                isDisabled={seconds <= 20}
                autoBuy={autoBuy}
                cardCost={ticketPrice}
                handleClick={onBuy}
            />
        </NextGamePanelContainer>
    );
};
